import styled, { css } from 'styled-components';
import { NavLink } from 'react-router-dom';
import { Image as SCImage } from '@sitecore-jss/sitecore-jss-react';
import { text_xscap, heading_m, text_s, text_l, mq, svg, linkCard } from '_utils/styles';
import { spacing } from '_utils/props';
import ContentRichText from '../ContentRichText';

const container = css`
  width: 100%;
  display: flex;

  flex: 1 0 auto;
  flex-direction: row;
  color: inherit;
  text-decoration: none;
  position: relative;

  ${mq('lg')} {
    height: 100%;
  }
`;

export const Card = styled(NavLink)`
  ${({ theme }) => linkCard({ theme })}
  ${({ theme }) => spacing({ theme, $p: 3 })}
  ${container}
  display: block;
  @media print {
    page-break-inside: avoid;
  }
`;

export const Listing = styled.div`
  ${container}
  border-bottom: ${({ theme }) => theme.card.borderDashed};

  /* juggle padding settings to keep border at bottom and 
   work within grid gutters at various breakpoints  */
  ${({ theme }) => spacing({ theme, $pb: 1 })}
  
  ${mq('md')} {
    ${({ theme }) => spacing({ theme, $pt: 1 })}
    ${({ theme }) => spacing({ theme, $pb: 2 })}
  }

  ${mq('lg')} {
    ${({ theme }) => spacing({ theme, $pt: 0 })}
    ${({ theme }) => spacing({ theme, $pb: 3 })}
  }

  /* @media (hover: hover) {
    &:hover {
      border-bottom: ${({ theme }) => theme.card.borderDashedHover};
    }
  } */
  @media print {
    page-break-inside: avoid;

  }
`;

export const ImageColumn = styled.div`
  flex: none;
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;

export const ImageContainer = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  background-color: ${({ theme }) => theme.colors.background.senary};
  position: relative;
  overflow: hidden;
  ${({ theme }) => spacing({ theme, $mr: 4 })}

  ${mq('md')} {
    width: 128px;
    height: 128px;
  }

  ${Listing}.publication &,
  ${Card}.publication & {
    border-radius: 0;
    background-color: transparent;
  }

  ${Listing}.focus &,
  ${Card}.focus & {
    background-color: ${({ theme }) => theme.colors.background.tertiary};
  }
`;

export const Image = styled(SCImage)`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  object-fit: cover;
  font-family: 'object-fit: cover;';

  ${Listing}.publication &,
  ${Card}.publication & {
    object-fit: contain;
    font-family: 'object-fit: contain;';
  }

  ${Listing}.focus &,
  ${Card}.focus & {
    width: 75%;
    height: 75%;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    object-fit: contain;
    font-family: 'object-fit: contain;';
  }
`;

export const DetailsColumn = styled.div`
  flex: 1 1 auto;
  display: block;
`;

export const Category = styled.p`
  ${text_xscap}
  margin: 0;
`;

const IconContainerStyles = css`
  display: block;
  color: inherit;
  text-decoration: none;

  &.title-is-link:hover {
    color: ${({ theme }) => theme.card.action};
  }
`;

export const IconContainer = styled.span`
  ${IconContainerStyles}
`;

export const LinkIconContainer = styled(NavLink)`
  ${IconContainerStyles}
`;

export const AttributesWrapper = styled.span`
  ${text_s}
  margin: 0;
  display: block;
  color: ${({ theme }) => theme.card.secondary};

  &:not(:first-child) {
    ${({ theme }) => spacing({ theme, $mt: 1 })}
  }
`;

export const ArrowWrapper = styled.span`
  ${svg(14, 14)}
  width: 14px;
  margin-left: 12px;
  display: inline-block;

  path {
    fill: ${({ theme }) => theme.card.action};
  }
`;

export const Title = styled.span`
  ${heading_m}

  &:not(:first-child) {
    ${({ theme }) => spacing({ theme, $mt: 1 })}
  }
`;

export const TitleIconWrapper = styled.span`
  white-space: nowrap;
  line-height: 0;
`;

export const Description = styled(ContentRichText)`
  ${text_s}
  margin: 0;
  &:not(:first-child) {
    ${({ theme }) => spacing({ theme, $mt: 1 })}
  }
  color: ${({ theme }) => theme.colors.element.quaternary};

  > :last-child {
    margin-bottom: 0;
  }
`;

export const DateWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
`;

export const Date = styled.span`
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  /* text needs a bit of help to fit */
  transform: scale(0.9);

  ${mq('md')} {
    transform: scale(1);
  }
`;

export const DateDay = styled.span`
  ${text_s}
  line-height: 1;
  margin: 0;
  text-transform: uppercase;
`;
export const DateNumber = styled.span`
  ${text_l}
  line-height: 1;
  margin: 0;
`;
export const DateMonth = styled.span`
  ${text_s}
  line-height: 1;
  color: ${({ theme }) => theme.colors.all.blue};
  margin: 0;
  text-transform: uppercase;
`;

export const LogoWrapper = styled.span`
  ${svg(39, 40)}

  position: absolute;
  width: 100%;
  height: 100%;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);

  path {
    fill: ${({ theme }) => theme.colors.all.deepBlueTint15};
  }
`;
